/**
 * PH Restaurant POS - Offline Database (Dexie.js)
 * IndexedDB wrapper for offline-first functionality
 */
import Dexie from 'dexie';

// Create database instance
const db = new Dexie('PHRestaurantPOS');

// Database schema - Version 1
db.version(1).stores({
  // Menu cache - synced from server
  categories: 'id, name, sortOrder, isActive, cachedAt',
  menuItems: 'id, categoryId, name, price, isActive, cachedAt',
  
  // Tables cache
  tables: 'id, outletId, name, status, capacity, cachedAt',
  
  // Local tickets (created offline)
  tickets: 'id, offlineId, outletId, tableId, status, total, createdAt, syncStatus, syncedAt, idempotencyKey',
  
  // Local order items
  orderItems: 'id, ticketId, menuItemId, name, price, quantity, lineTotal, isVoided',
  
  // Local payments
  payments: 'id, ticketId, method, amount, tendered, changeGiven, status, idempotencyKey',
  
  // Sync queue - operations pending sync
  syncQueue: '++id, type, entityId, operation, payload, createdAt, attempts, lastAttempt, status, error',
  
  // Cash movements (offline)
  cashMovements: 'id, offlineId, outletId, shiftId, type, amount, reason, createdAt, syncStatus, idempotencyKey',
  
  // Metadata
  syncMeta: 'key, value, updatedAt'
});

// Database upgrade for version 2 - must include all stores
db.version(2).stores({
  // Menu cache - synced from server
  categories: 'id, name, sortOrder, isActive, cachedAt',
  menuItems: 'id, categoryId, name, price, isActive, cachedAt',
  
  // Tables cache
  tables: 'id, outletId, name, status, capacity, cachedAt',
  
  // Local tickets (created offline)
  tickets: 'id, offlineId, outletId, tableId, status, total, createdAt, syncStatus, syncedAt, idempotencyKey',
  
  // Local order items
  orderItems: 'id, ticketId, menuItemId, name, price, quantity, lineTotal, isVoided',
  
  // Local payments
  payments: 'id, ticketId, method, amount, tendered, changeGiven, status, idempotencyKey',
  
  // Sync queue - operations pending sync
  syncQueue: '++id, type, entityId, operation, payload, createdAt, attempts, lastAttempt, status, error',
  
  // Cash movements (offline)
  cashMovements: 'id, offlineId, outletId, shiftId, type, amount, reason, createdAt, syncStatus, idempotencyKey',
  
  // Metadata
  syncMeta: 'key, value, updatedAt'
}).upgrade(tx => {
  // Migration logic if needed
});

/**
 * Sync queue operation types
 */
export const SyncOperationType = {
  CREATE_TICKET: 'CREATE_TICKET',
  ADD_ITEM: 'ADD_ITEM',
  VOID_ITEM: 'VOID_ITEM',
  VOID_TICKET: 'VOID_TICKET',
  PROCESS_PAYMENT: 'PROCESS_PAYMENT',
  CASH_IN: 'CASH_IN',
  CASH_OUT: 'CASH_OUT',
  DEPOSIT: 'DEPOSIT',
  OPEN_SHIFT: 'OPEN_SHIFT',
  CLOSE_SHIFT: 'CLOSE_SHIFT'
};

/**
 * Sync status enum
 */
export const SyncStatus = {
  PENDING: 'pending',
  SYNCING: 'syncing',
  SYNCED: 'synced',
  FAILED: 'failed',
  CONFLICT: 'conflict'
};

/**
 * Generate UUID v4
 */
export function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * Generate idempotency key
 */
export function generateIdempotencyKey(prefix = 'op') {
  return `${prefix}-${generateUUID()}-${Date.now()}`;
}

/**
 * Menu Cache Operations
 */
export const menuCache = {
  async cacheCategories(categories) {
    const now = new Date().toISOString();
    const items = categories.map(cat => ({
      ...cat,
      cachedAt: now
    }));
    await db.categories.bulkPut(items);
    await db.syncMeta.put({ key: 'categories_cached_at', value: now, updatedAt: now });
  },

  async cacheMenuItems(items) {
    const now = new Date().toISOString();
    const itemsWithMeta = items.map(item => ({
      ...item,
      cachedAt: now
    }));
    await db.menuItems.bulkPut(itemsWithMeta);
    await db.syncMeta.put({ key: 'menu_items_cached_at', value: now, updatedAt: now });
  },

  async getCategories() {
    return await db.categories.orderBy('sortOrder').toArray();
  },

  async getMenuItems(categoryId = null) {
    if (categoryId) {
      return await db.menuItems.where('categoryId').equals(categoryId).toArray();
    }
    return await db.menuItems.toArray();
  },

  async getMenuItem(id) {
    return await db.menuItems.get(id);
  },

  async getCacheAge() {
    const meta = await db.syncMeta.get('menu_items_cached_at');
    if (!meta) return Infinity;
    return Date.now() - new Date(meta.value).getTime();
  },

  async clearCache() {
    await db.categories.clear();
    await db.menuItems.clear();
    await db.syncMeta.where('key').startsWithIgnoreCase('categories').delete();
    await db.syncMeta.where('key').startsWithIgnoreCase('menu').delete();
  }
};

/**
 * Tables Cache Operations
 */
export const tablesCache = {
  async cacheTables(tables) {
    const now = new Date().toISOString();
    const items = tables.map(table => ({
      ...table,
      cachedAt: now
    }));
    await db.table('tables').bulkPut(items);
    await db.syncMeta.put({ key: 'tables_cached_at', value: now, updatedAt: now });
  },

  async getTables(outletId = null) {
    if (outletId) {
      return await db.table('tables').where('outletId').equals(outletId).toArray();
    }
    return await db.table('tables').toArray();
  },

  async getTable(id) {
    return await db.table('tables').get(id);
  },

  async updateTableStatus(id, status, currentTicketId = null) {
    await db.table('tables').update(id, { status, currentTicketId });
  }
};

/**
 * Offline Ticket Operations
 */
export const offlineTickets = {
  async createTicket(ticketData) {
    const id = generateUUID();
    const offlineId = `OFF-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const idempotencyKey = generateIdempotencyKey('ticket');
    
    const ticket = {
      id,
      offlineId,
      idempotencyKey,
      outletId: ticketData.outletId,
      tableId: ticketData.tableId || null,
      status: 'open',
      subtotal: 0,
      discount: 0,
      tax: 0,
      serviceCharge: 0,
      total: 0,
      guestCount: ticketData.guestCount || 1,
      createdAt: new Date().toISOString(),
      syncStatus: SyncStatus.PENDING,
      syncedAt: null
    };
    
    await db.tickets.add(ticket);
    
    // Add to sync queue
    await syncQueue.addToQueue(SyncOperationType.CREATE_TICKET, id, ticket);
    
    return ticket;
  },

  async addItem(ticketId, item) {
    const id = generateUUID();
    const orderItem = {
      id,
      ticketId,
      menuItemId: item.id,
      name: item.name,
      price: item.price,
      quantity: item.quantity || 1,
      lineTotal: item.price * (item.quantity || 1),
      notes: item.notes || '',
      isVoided: false
    };
    
    await db.orderItems.add(orderItem);
    await this.recalculateTicket(ticketId);
    
    // Add to sync queue
    await syncQueue.addToQueue(SyncOperationType.ADD_ITEM, id, { ticketId, item: orderItem });
    
    return orderItem;
  },

  async voidItem(ticketId, itemId, reason) {
    await db.orderItems.update(itemId, { isVoided: true, voidReason: reason });
    await this.recalculateTicket(ticketId);
    
    // Add to sync queue
    await syncQueue.addToQueue(SyncOperationType.VOID_ITEM, itemId, { ticketId, itemId, reason });
  },

  async recalculateTicket(ticketId) {
    const items = await db.orderItems.where('ticketId').equals(ticketId).toArray();
    const activeItems = items.filter(i => !i.isVoided);
    
    const subtotal = activeItems.reduce((sum, item) => sum + item.lineTotal, 0);
    const serviceCharge = subtotal * 0.10; // 10%
    const taxableAmount = subtotal + serviceCharge;
    const tax = taxableAmount * 0.12 / 1.12; // 12% VAT inclusive
    const total = subtotal + serviceCharge;
    
    await db.tickets.update(ticketId, { subtotal, serviceCharge, tax, total });
    
    return { subtotal, serviceCharge, tax, total };
  },

  async getTicket(id) {
    const ticket = await db.tickets.get(id);
    if (!ticket) return null;
    
    const items = await db.orderItems.where('ticketId').equals(id).toArray();
    const payments = await db.payments.where('ticketId').equals(id).toArray();
    
    return { ...ticket, items, payments };
  },

  async getOpenTickets(outletId) {
    return await db.tickets
      .where('outletId').equals(outletId)
      .and(t => t.status === 'open' || t.status === 'hold')
      .toArray();
  },

  async getPendingSyncTickets() {
    return await db.tickets
      .where('syncStatus').equals(SyncStatus.PENDING)
      .toArray();
  },

  async markSynced(ticketId, serverTicketId) {
    await db.tickets.update(ticketId, {
      syncStatus: SyncStatus.SYNCED,
      syncedAt: new Date().toISOString(),
      serverTicketId
    });
  },

  async processPayment(ticketId, paymentData) {
    const id = generateUUID();
    const idempotencyKey = generateIdempotencyKey('pay');
    
    const payment = {
      id,
      ticketId,
      method: paymentData.method,
      amount: paymentData.amount,
      tendered: paymentData.tendered || null,
      changeGiven: paymentData.change || null,
      status: 'paid',
      idempotencyKey,
      createdAt: new Date().toISOString()
    };
    
    await db.payments.add(payment);
    
    // Check if fully paid
    const ticket = await db.tickets.get(ticketId);
    const payments = await db.payments.where('ticketId').equals(ticketId).toArray();
    const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
    
    if (totalPaid >= ticket.total) {
      await db.tickets.update(ticketId, { status: 'paid' });
    }
    
    // Add to sync queue
    await syncQueue.addToQueue(SyncOperationType.PROCESS_PAYMENT, id, { ticketId, payment });
    
    return payment;
  }
};

/**
 * Sync Queue Operations
 */
export const syncQueue = {
  async addToQueue(type, entityId, payload) {
    await db.syncQueue.add({
      type,
      entityId,
      operation: type,
      payload: JSON.stringify(payload),
      createdAt: new Date().toISOString(),
      attempts: 0,
      lastAttempt: null,
      status: SyncStatus.PENDING,
      error: null
    });
  },

  async getPendingOperations() {
    return await db.syncQueue
      .where('status').anyOf([SyncStatus.PENDING, SyncStatus.FAILED])
      .and(op => op.attempts < 5) // Max 5 retries
      .sortBy('createdAt');
  },

  async markSyncing(id) {
    await db.syncQueue.update(id, { status: SyncStatus.SYNCING });
  },

  async markSynced(id) {
    await db.syncQueue.update(id, { 
      status: SyncStatus.SYNCED,
      lastAttempt: new Date().toISOString()
    });
  },

  async markFailed(id, error) {
    const op = await db.syncQueue.get(id);
    await db.syncQueue.update(id, { 
      status: SyncStatus.FAILED,
      attempts: (op?.attempts || 0) + 1,
      lastAttempt: new Date().toISOString(),
      error: error?.message || String(error)
    });
  },

  async getQueueStats() {
    const all = await db.syncQueue.toArray();
    return {
      total: all.length,
      pending: all.filter(o => o.status === SyncStatus.PENDING).length,
      syncing: all.filter(o => o.status === SyncStatus.SYNCING).length,
      synced: all.filter(o => o.status === SyncStatus.SYNCED).length,
      failed: all.filter(o => o.status === SyncStatus.FAILED).length
    };
  },

  async clearSynced() {
    await db.syncQueue.where('status').equals(SyncStatus.SYNCED).delete();
  }
};

/**
 * Cash Movements (Offline)
 */
export const offlineCashMovements = {
  async recordCashMovement(type, data) {
    const id = generateUUID();
    const offlineId = `CASH-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const idempotencyKey = generateIdempotencyKey('cash');
    
    const movement = {
      id,
      offlineId,
      idempotencyKey,
      outletId: data.outletId,
      shiftId: data.shiftId,
      type, // CASH_IN, CASH_OUT, DEPOSIT
      amount: data.amount,
      reason: data.reason,
      bankName: data.bankName || null,
      bankReference: data.bankReference || null,
      createdAt: new Date().toISOString(),
      syncStatus: SyncStatus.PENDING
    };
    
    await db.cashMovements.add(movement);
    
    // Add to sync queue
    await syncQueue.addToQueue(type, id, movement);
    
    return movement;
  },

  async getPendingMovements() {
    return await db.cashMovements
      .where('syncStatus').equals(SyncStatus.PENDING)
      .toArray();
  }
};

/**
 * Sync Metadata Operations
 */
export const syncMeta = {
  async get(key) {
    const meta = await db.syncMeta.get(key);
    return meta?.value;
  },

  async set(key, value) {
    await db.syncMeta.put({ key, value, updatedAt: new Date().toISOString() });
  },

  async getLastSyncTime() {
    return await this.get('last_sync_time');
  },

  async setLastSyncTime() {
    await this.set('last_sync_time', new Date().toISOString());
  },

  async isOnline() {
    const status = await this.get('is_online');
    return status !== 'false';
  },

  async setOnlineStatus(isOnline) {
    await this.set('is_online', String(isOnline));
  }
};

/**
 * Clear all local data
 */
export async function clearAllData() {
  await db.categories.clear();
  await db.menuItems.clear();
  await db.table('tables').clear();
  await db.tickets.clear();
  await db.orderItems.clear();
  await db.payments.clear();
  await db.syncQueue.clear();
  await db.cashMovements.clear();
  await db.syncMeta.clear();
}

/**
 * Export database stats with error handling
 */
export async function getDatabaseStats() {
  const safeCount = async (table) => {
    try {
      return await table.count();
    } catch (e) {
      console.warn(`Failed to count ${table.name}:`, e);
      return 0;
    }
  };

  return {
    categories: await safeCount(db.categories),
    menuItems: await safeCount(db.menuItems),
    tables: await safeCount(db.table('tables')),
    tickets: await safeCount(db.tickets),
    orderItems: await safeCount(db.orderItems),
    payments: await safeCount(db.payments),
    syncQueue: await syncQueue.getQueueStats(),
    cashMovements: await safeCount(db.cashMovements)
  };
}

export default db;
